'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { env } from '~/env'
import { searchLocations, type LocationSearchResult } from '~/lib/geocoding'

interface AddressAutocompleteProps {
  value: string
  onChange: (address: string, latitude: number, longitude: number, zipCode: string) => void
  placeholder?: string
  className?: string
}

export default function AddressAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Enter address",
  className = ""
}: AddressAutocompleteProps) {
  const [searchResults, setSearchResults] = useState<LocationSearchResult[]>([])
  const [showResults, setShowResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [inputValue, setInputValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Always sync local input value with the value prop from parent
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Address search function
  const performAddressSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([])
      return
    }

    try {
      const results = await searchLocations(query, env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN, {
        country: 'us',
        types: 'place,address,poi',
        limit: 5,
      })
      setSearchResults(results)
    } catch (error) {
      console.error('Error searching addresses:', error)
      setSearchResults([])
    }
  }, [])

  // Handle address selection
  const handleAddressSelect = useCallback((result: LocationSearchResult) => {
    // Extract zip code from the place name
    const zipCodeRegex = /\b\d{5}\b/
    const zipCodeMatch = zipCodeRegex.exec(result.place_name)
    const zipCode = zipCodeMatch ? zipCodeMatch[0] : ''
    
    // Get coordinates from the result
    const [longitude, latitude] = result.center
    
    // Update input value to show the selected address
    setInputValue(result.place_name)
    
    // Call onChange with the FULL Mapbox address data
    onChange(result.place_name, latitude, longitude, zipCode)
    
    // Clear search results
    setSearchResults([])
    setShowResults(false)
    setSelectedIndex(-1)
  }, [onChange])

  // Handle input changes with debouncing (but don't call onChange)
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue) // Only update local state for search
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // If input is cleared, clear results
    if (!newValue.trim()) {
      setSearchResults([])
      setShowResults(false)
      return
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      void performAddressSearch(newValue)
      setShowResults(true)
    }, 300)
  }, [performAddressSearch])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showResults || searchResults.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          const selectedResult = searchResults[selectedIndex]
          if (selectedResult) {
            handleAddressSelect(selectedResult)
          }
        }
        break
      case 'Escape':
        setShowResults(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }, [showResults, searchResults, selectedIndex, handleAddressSelect])

  // Handle click outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        name="mapbox-result"
        value={inputValue}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (searchResults.length > 0) {
            setShowResults(true)
          }
        }}
        placeholder={placeholder}
        className={`mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:bg-white focus:outline-hidden focus:ring-primary sm:text-sm ${className}`}
      />
      
      {/* Search Results Dropdown */}
      {showResults && (
        <div
          ref={resultsRef}
          className="absolute z-50 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-auto border border-gray-200"
        >
          {searchResults.length > 0 ? (
            <ul className="py-1">
              {searchResults.map((result, index) => (
                <li
                  key={result.id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                    index === selectedIndex ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'
                  }`}
                  onClick={() => handleAddressSelect(result)}
                >
                  <div className="font-medium">{result.text}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {result.place_name}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              No addresses found.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
